/**
 * @file `MiniDeliveryBar` — a proportional delivery-health bar for the
 *       campaigns list (#1606 Part 12), plus `worstStateOf` for the "needs
 *       attention" flag.
 * @module @labcraft/admin/ui/emails/mini-delivery-bar
 *
 * Renders the per-state aggregates (delivered / delayed / problems)
 * as a single stacked bar so an operator scanning the list sees a campaign's
 * delivery health at a glance. `worstStateOf` collapses the aggregates to a
 * single severity — `bad` (bounces / complaints / failures), `warn` (delays),
 * or `ok` — which drives the row accent + the real "needs attention" filter.
 */

import type { JSX } from 'react'
import type { CampaignSummary } from '../../shared/emails_routes.ts'

/** Collapse a campaign's delivery aggregates to a single severity. */
export function worstStateOf(c: CampaignSummary): 'bad' | 'warn' | 'ok' {
  if (c.failedCount > 0 || c.bouncedCount > 0 || c.complainedCount > 0) {
    return 'bad'
  }
  if (c.delayedCount > 0) return 'warn'
  return 'ok'
}

/** Render the proportional delivery-health bar for one campaign. */
export function MiniDeliveryBar({
  campaign,
}: {
  campaign: CampaignSummary
}): JSX.Element {
  const total = Math.max(campaign.sentCount, 1)
  const problems =
    campaign.bouncedCount + campaign.complainedCount + campaign.failedCount
  const segments: ReadonlyArray<{ cls: string; n: number }> = [
    { cls: 'dbar__ok', n: campaign.deliveredCount },
    { cls: 'dbar__warn', n: campaign.delayedCount },
    { cls: 'dbar__bad', n: problems },
  ]
  const title =
    `${campaign.deliveredCount} delivered · ` +
    `${campaign.delayedCount} delayed · ${problems} problems`

  return (
    <span className="dbar" title={title}>
      {segments.map((s, i) =>
        s.n > 0 ? (
          <span
            key={i}
            className={s.cls}
            style={{ width: `${(s.n / total) * 100}%` }}
          />
        ) : null,
      )}
    </span>
  )
}
